<template>
	<div>
		<AgItem class="flex_1"
				:columnDefs="columnDefs"
				:rowData="rowData"
				v-on:sortChanged="sortChanged"
				:frameworkComponents="frameworkComponents"
		></AgItem>
		<Pagination
			:totalNum="totalNum"
			:pageSize="pageSize"
			:sizeChange="sizeChange"
			:currentChange="currentChange"
			:currentPage="page"
		></Pagination>
		<Modal v-if="showModal" v-on:close="close">
			<template slot="body">
				<CancelAuctionModal v-on:close="close" :auctionId="this.auctionId"></CancelAuctionModal>
			</template>
		</Modal>
	</div>
</template>
<script>
import AgItem from "@/components/templates/agGrid/AgItem.vue";
import {asLocalizedDate, asMoneyAmount, asPercentage} from "@/components/templates/agGrid/formatter/valueFormatters";
import Modal from "@/components/templates/modal/Modal.vue";
import CancelAuctionModal from "@/components/templates/seller/sell/CancelAuctionModal.vue";
import Pagination from "@/components/templates/common/Pagination";
import PaginationParent from "@/components/templates/common/PaginationParent";
import {enumDef} from "@/common/enumDefs";
import {sellApi} from "@/common/api/sellApi";

export default {
	extends: PaginationParent,
	props: {
		frameworkComponents: Object,
	},
	components: { AgItem, CancelAuctionModal, Modal, Pagination },
	data() {
		return {
			rowData: null,
			columnDefs: [],
			sorting: [],
			showModal: false,
			auctionId: null
		};
	},
	beforeMount() {
		this.setColumnDefs();
		this.getList();
	},
	methods: {
		getGridApi(gApi) {
			this.gridApi = gApi;
		},
		setColumnDefs() {
			this.columnDefs = [
								{
					headerName: this.$t("common.debtor"),
					field: "debtorName",
					minWidth: 200,
					sortable: true,
					nameType: "debtorName",
					idType: "debtorId",
					cellRendererFramework: "CompanyTem"
				},
				{
					headerName: this.$t("common.type"),
					field: "receivableType",
					minWidth: 65,
					maxWidth: 80,
					sortable: true,
					cellRendererFramework: "EnumAsIcon",
					enumDef: enumDef.receivableType
				},
				{
					headerName: this.$t("common.invoiceNumber"),
					field: "invoiceNumber",
					minWidth: 175,
					sortable: true,
					cellRendererFramework: "ItemDetail",
				},
				{
					headerName: this.$t("common.contractNumber"),
					field: "installmentContractNumber",
					minWidth: 150,
					sortable: true,
				},
				{
					headerName: this.$t("common.dueDate"),
					field: "dueDate",
					valueFormatter: asLocalizedDate(),
					sortable: true,
					minWidth: 140,
					maxWidth: 160,
				},
				{
					headerName: this.$t("common.amount"),
					headerClass: "align-right",
					cellStyle: { "justify-content": "flex-end" },
					minWidth: 100,
					maxWidth: 120,
					field: "receivableAmount",
					valueFormatter: asMoneyAmount(),
					sortable: true,
				},
				{
					headerName: this.$t("common.ccy"),
					minWidth: 70,
					maxWidth: 100,
					field: "currency",
					sortable: true,
				},
				{
					headerName: this.$t("common.type"),
					field: "postTrade",
					minWidth: 200,
					cellRendererFramework: "EnumAsText",
					enumDef: enumDef.postTradeType,
					sortable: true,
				},
				{
					headerName: this.$t("common.status"),
					field: "status",
					minWidth: 100,
					cellRendererFramework: "EnumAsText",
					enumDef: enumDef.auctionStatus,
					sortable: true,
				},
				{
					headerName: this.$t("common.timeLeft"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.remainingIntervalTime.tooltip")
					},
					minWidth: 120,
					maxWidth: 150,
					field: "endTime",
					cellRendererFramework: "TimeLeftCountdown",
					cellRendererParams: {
						endTimeGetter: (data) =>
							data.status === enumDef.auctionStatus.Public ? data.startTime :
							data.status === enumDef.auctionStatus.Selling ? data.endTime :
							"",
						onExpire: this.auctionExpired
					},
					sortable: true,
				},
				{
					headerName: this.$t("common.retention"),
					headerClass: "align-right",
					sortable: true,
					field: "allowedRetentionRate",
					cellStyle: { "justify-content": "flex-end" },
					minWidth: 100,
					valueFormatter: asPercentage({ showEmptyAsNotAvailable: true, decimals: 0 }),
				},
				{
					headerName: this.$t("seller.sell.table.header.interestInPercent"),
					headerComponentParams: {
						tooltip: this.$t("seller.sell.interestAndServiceFee.tooltip"),
						showTooltipIcon: true
					},
					headerClass: "align-right",
					sortable: true,
					field: "totalInterestRate",
					cellStyle: { "justify-content": "flex-end" },
					minWidth: 130,
					valueFormatter: asPercentage(),
				},
				{
					headerName: this.$t("seller.sell.table.header.serviceFeeInPercent"),
					headerComponentParams: {
						tooltip: this.$t("seller.sell.interestAndServiceFee.tooltip"),
						showTooltipIcon: true
					},
					headerClass: "align-right",
					sortable: true,
					field: "serviceFeeRate",
					cellStyle: { "justify-content": "flex-end" },
					minWidth: 175,
					valueFormatter: asPercentage(),
				},
				{
					headerName: this.$t("seller.sell.table.header.withRetention.instantPayout"),
					headerClass: "align-right",
					sortable: true,
					field: "instantPayoutInclRetention",
					cellStyle: { "justify-content": "flex-end" },
					minWidth: 210,
					valueFormatter: asMoneyAmount({ currencyField: "currency"}),
				},
				{
					headerName: this.$t("seller.sell.table.header.withRetention.retentionPayout"),
					headerClass: "align-right",
					sortable: true,
					field: "retentionPayout",
					cellStyle: { "justify-content": "flex-end" },
					minWidth: 200,
					valueFormatter: asMoneyAmount( { currencyField: "currency", showZeroAsNotAvailable: true } ),
				},
				{
					headerName: this.$t("common.actions"),
					minWidth: 120,
					maxWidth: 120,
					pageType: "sell",
					cellRendererFramework: "SellCurrentTableOperation",
					cellRendererParams: {
						showCancelModal: this.showCancelModal
					}
				}
			];
		},
		showCancelModal(data) {
			this.showModal = true;
			this.auctionId = data.auctionId;

		},
		close() {
			this.showModal = false;
		},
		sortChanged(sortData) {
			this.sorting = sortData;
			this.initPage();
			this.getList();
		},
		getList() {
			let postData = {
				columns: [],
				filters: [],
				maxResults: this.maxResults,
				offset: this.offset,
				sorting: this.sorting,
			};
			sellApi.inspection.listSelling(postData)
				.then(res => {
					this.setTotalNum(res.recordsTotal);
					this.rowData = res.data.map(item => {
						return {
							...item,
							expired: false
						}
					});
				})
				.catch(this.toast);
		},
		auctionExpired(data) {
			data.expired = true;
		}
	}
};
</script>
